import userSession from '@webapp-next/lib/user-session';
import {get} from '@webapp-next/lib/request/webapp/apiv2';
// Do NOT remove. This is for unit testing purposes
import * as thisModule from './people-edit-pay-detail-item.js';
import useEmploymentStore from '@/modules/people/store/employment';
import useRootStore from '@/modules/people/store/root';

export const ERROR_TYPES_ENUM = {
  PAY_CENTER: 'paycenter',
  PAY_PERIOD: 'payperiod',
  PAY_LOCATION: 'paylocation',
};

/**
 * Structures locations so that they can be used by the multi-selector component
 *
 * @param {Object[]} locations
 * @param {Object} loggedUserAccessibleLocations
 * @return {Object[]}
 */
export function sanitizeLocations(locations, loggedUserAccessibleLocations) {
  if (!locations) {
    return [];
  }

  return locations.map(item => {
    return {
      id: item.Id,
      link: item.Id,
      label: item.CompanyName,
      active: item.Active,
      noRemovePermission: !loggedUserAccessibleLocations[item.Id],
      canSelect: !!loggedUserAccessibleLocations[item.Id],
      parentId: 0,
      children: [],
    };
  });
}

/**
 * Structures workDetail locations so that they can be used by the multi-selector component.
 * Unlike sanitizeLocations, sanitizeWorkDetailLocations assumes each location
 *  already has the noRemovePermission and canSelect properties.
 *
 * @param {Object[]} locations
 * @return {Object[]}
 */
export function sanitizeWorkDetailLocations(locations) {
  if (!locations) {
    return [];
  }

  return locations.map(item => {
    return {
      id: item.link,
      link: item.link,
      label: item.label,
      active: item.active,
      noRemovePermission: item.noRemovePermission,
      canSelect: item.canSelect,
      parentId: 0,
      children: [],
    };
  });
}

/**
 * Gets only locations accessible to the current user
 * @return {Object<string, boolean>}
 */
export function getLoggedUserAccessibleLocations() {
  return (userSession().MySetup.Workplace || []).reduce((reduced, { Id }) => {
    reduced[Id] = true;
    return reduced;
  }, {});
}

/**
 * Takes an array of contracts and an object of contractsIndexedByID
 * and inserts all the contracts into contractsIndexedByID.
 * Will add any new contracts from the array into the ID-indexed object.
 * Will also overwrite any existing contracts in the ID-indexed object.
 *
 * @param {Object[]} contracts
 * @param {Object} contractsIndexedByID
 * @return {Object}
 */
export function createContractsIndexedByIDObject(
  contracts = [],
  contractsIndexedByID = {}
) {
  const rootState = useRootStore();
  contracts.forEach(contract => {
    // Sync the term array from EmploymentTerms
    let matchedEmploymentTerm =
      rootState.employmentTermIndexedByFileName[contract.Code] || {};
    if (!contract.Id) {
      if (
        matchedEmploymentTerm &&
        !matchedEmploymentTerm.AlreadySyncedContracts
      ) {
        matchedEmploymentTerm.TermId = matchedEmploymentTerm.Id;
        matchedEmploymentTerm.Id = String(matchedEmploymentTerm.FileName);
        matchedEmploymentTerm.AlreadySyncedContracts = true;
      }
    } else {
      matchedEmploymentTerm = {};
    }

    // TODO: Remove the forced PeriodType
    const newContract = {
      ...contract,
      ...matchedEmploymentTerm,
    };
    contractsIndexedByID[newContract.Id] = newContract;
  });
  return contractsIndexedByID;
}

/**
 * Will fetch only contracts that it hasn't already fetched before
 * including contracts that have been modified.
 *
 * @return {Object}
 */
export const getOnlyNewOrUpdatedContracts = (function() {
  return async function() {
    return await thisModule.getContracts();
  };
})();

/**
 * Gets all the employment contracts.
 * The earliestModifiedTime parameter is used to fetch only the contracts that
 * have a 'modified' time that is more recent than the earliestModifiedTime provided.
 * @return {Promise<Object[]>}
 */
export async function getContracts() {
  const { data } = await get('api/v2/people/contracts');
  return data.contracts;
}

/**
 * Validate the pay period and pay center if it has been used in another employee pay detail.
 * @param {Object} payDetail
 *  @param {number} payDetail.payPeriod
 *  @param {number} payDetail.payCentre
 * @param {number} payDetailIndex - the index of which pay detail is being validated. -1 means it is a new pay detail
 * @param {boolean} payCentreOnly
 * @return {Object}
 */
export function validatePayPeriodPayCentre(
  payDetail,
  payDetailIndex,
  payCentreOnly = false
) {
  if ((!payDetail.payPeriod && !payCentreOnly) || !payDetail.payCentre) {
    return {};
  }

  const employmentState = useEmploymentStore();
  const { payPeriod, payCentre } = payDetail;
  const payDetailsList = employmentState.localPayDetail;

  const result = payCentreOnly
    ? payDetailsList.filter(
        (payDetail, index) =>
          index !== payDetailIndex &&
          payDetail.payCentre === payCentre &&
          payCentre
      )
    : payDetailsList.filter(
        (payDetail, index) =>
          index !== payDetailIndex &&
          payDetail.payPeriod === payPeriod &&
          payDetail.payCentre === payCentre &&
          payPeriod &&
          payCentre
      );

  return result[0];
}

/**
 * Assigns validation type and message on the pay detail object
 * @param {Object} error
 * @param {string} error.type
 * @param {string} error.value
 * @param {Object} payDetail
 */
export function setValidationError(error, payDetail) {
  const { type, value } = error;
  payDetail.validationError[type] = value;
}

/**
 * Clears validationError of a pay detail object
 * @param {Object} payDetail
 */
export function clearValidationError(payDetail) {
  payDetail.validationError = {};
}

/**
 * Check if the select level should be visible
 * @param {Object} contractLevels
 * @return {Boolean}
 */
export function checkSelectLevelVisible(contractLevels) {
  return contractLevels?.length > 0;
}

/**
 * Initialise level for pay detail
 * @param {Object} payDetail
 * @param {Object[]} levels
 */

export function setPayDetailLevel(payDetail, level) {
  payDetail.level = level;
}
